/** @jsx jsx */
import { Box, EmptyState, EmptyStateSadCircle, Flex, Heading } from "@eworkit/iris";
import { FC, useCallback, useContext, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { jsx } from "theme-ui";

import { InfinityList } from "@/components/InfinityList";
import { UserContext } from "@/components/User";
import { fetchConsultantProfileListJRA } from "@/data/consultant-profiles";
import { ConsultantProfile } from "@/data/consultant-profiles/types/ConsultantProfile";
import { JobRequest } from "@/data/job-requests/types/JobRequest";
import { InviteUserButton } from "@/views/Invitations";

import { useApplyWizard } from "../_components/ApplyWizardContext";
import { ApplyCandidatesListCard } from "./ApplyCandidatesListCard";

interface ApplyCandidatesListProps {
  jobRequest: JobRequest;
  query?: string;
  designatedCandidate?: string;
}

export const ApplyCandidatesList: FC<ApplyCandidatesListProps> = ({ jobRequest, query, designatedCandidate }) => {
  const [applicationDraftsExist, setApplicationDraftsExist] = useState(false);
  const [candidatesListEmpty, setCandidatesListEmpty] = useState(true);

  const { t } = useTranslation("session");

  const { contextCompany } = useContext(UserContext);
  const companyId = contextCompany?.id;

  const scrollContainer = useRef(null);

  const { setProfile, goToNextStep } = useApplyWizard();

  const handleCandidateAdded = useCallback(
    (profile: ConsultantProfile) => {
      setProfile(profile);
      goToNextStep();
    },
    [setProfile, goToNextStep],
  );

  return (
    <Flex flexDirection="column" ref={scrollContainer}>
      {applicationDraftsExist && (
        <Box mb={"sm"}>
          <Heading size={["h-sm", null, "h-md"]}>Draft Applications</Heading>
        </Box>
      )}
      <InfinityList
        renderRecord={cp => (
          <Box key={cp.id} mb={[null, null, "xs"]}>
            <ApplyCandidatesListCard key={cp.id} profile={cp} jobRequest={jobRequest} />
          </Box>
        )}
        fetchFunction={fetchConsultantProfileListJRA}
        fetchParams={{
          size: 20,
          companyId,
          query,
          jobRequestId: jobRequest.id,
          queryType: "FULL",
          isApplicationInDraft: true,
        }}
        deps={[companyId, query, jobRequest]}
        scrollContainer={scrollContainer}
        emptyState={() => null}
        handleItemsCountChange={count => setApplicationDraftsExist(count > 0)}
        noResultsHeader={""}
        hideInitialLoader={true}
      />

      {applicationDraftsExist && !candidatesListEmpty && (
        <Box mb={"sm"} mt={"sm"}>
          <Heading size={["h-sm", null, "h-md"]}>Candidates</Heading>
        </Box>
      )}
      <InfinityList
        renderRecord={cp => (
          <Box key={cp.id} mb={[null, null, "xs"]}>
            <ApplyCandidatesListCard key={cp.id} profile={cp} jobRequest={jobRequest} />
          </Box>
        )}
        fetchFunction={fetchConsultantProfileListJRA}
        fetchParams={{
          size: 20,
          companyId,
          query,
          jobRequestId: jobRequest.id,
          queryType: "FULL",
          isApplicationInDraft: false,
        }}
        deps={[companyId, query, jobRequest]}
        scrollContainer={scrollContainer}
        noResultsHeader={applicationDraftsExist ? "" : t("JOB_REQUESTS.APPLY.CONSULTANT_PROFILE_FORM.NO_CANDIDATES")}
        emptyState={
          applicationDraftsExist ? (
            () => null
          ) : (
            <Box px="sm" sx={{ py: [56, null, 80] }}>
              <EmptyState
                icon={<EmptyStateSadCircle />}
                title={t("JOB_REQUESTS.APPLY.WIZARD.NO_CANDIDATES")}
                content={
                  designatedCandidate
                    ? t("JOB_REQUESTS.APPLY.WIZARD.NO_DESIGNATED_CANDIDATES_INFO")
                    : t("JOB_REQUESTS.APPLY.WIZARD.NO_CANDIDATES_INFO")
                }
                action={
                  <InviteUserButton variant="primary" onSuccess={handleCandidateAdded}>
                    {t("JOB_REQUESTS.APPLY.WIZARD.NEW_CANDIDATE")}
                  </InviteUserButton>
                }
              />
            </Box>
          )
        }
        handleItemsCountChange={count => setCandidatesListEmpty(count === 0)}
      />
    </Flex>
  );
};
